import { Table, Tbody, Td, Th, Thead, Tr } from '@ff-it/ui';
import type { Key, ReactElement, ReactNode } from 'react';
import type { BandKey, BandSet, DayType, HourRange } from '../types';
import { BAND_COLUMN_WIDTH, dayTypeLabels } from './const';
import { getBandGroups, getBandKey, getBandlabel } from './util';

export type BandRateColumn<T extends HourRange> = {
  key: Key;
  header: ReactNode;
  render: (band: T, dayType: DayType, bandKey: BandKey) => ReactNode;
  /** Per-cell class, for matrices that tint the cell itself rather than its
   * contents (distribution drift). */
  cellClassName?: (band: T, dayType: DayType, bandKey: BandKey) => string | undefined;
};

/** Shared read/edit matrix for a fixed band geometry. Callers provide the
 * columns (outlets, row sides, etc.) and their rate-cell renderer. */
export function BandRateTable<T extends HourRange>({
  bands,
  columns,
  fixed = false,
}: {
  bands: BandSet<T>;
  columns: BandRateColumn<T>[];
  fixed?: boolean;
}): ReactElement {
  return (
    <Table size="xs" textSize="sm" border="subtle" fixed={fixed}>
      <colgroup>
        <col style={{ width: `${BAND_COLUMN_WIDTH}px` }} />
      </colgroup>
      <Thead variant="default">
        <Tr>
          <Th />
          {columns.map((column) => (
            <Th key={column.key} textAlign="right">
              {column.header}
            </Th>
          ))}
        </Tr>
      </Thead>
      {getBandGroups(bands).map(({ dayType, bands: groupBands }) => (
        <Tbody key={dayType}>
          <Tr>
            <Th colSpan={columns.length + 1} style={{ background: 'var(--colors-gray50)' }}>
              {dayTypeLabels[dayType]}
            </Th>
          </Tr>
          {groupBands.map((band) => {
            const bandKey = getBandKey(dayType, band);
            return (
              <Tr key={bandKey}>
                <Td>{getBandlabel(band)}</Td>
                {columns.map((column) => (
                  <Td key={column.key} textAlign="right" className={column.cellClassName?.(band, dayType, bandKey)}>
                    {column.render(band, dayType, bandKey)}
                  </Td>
                ))}
              </Tr>
            );
          })}
        </Tbody>
      ))}
    </Table>
  );
}
